import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState } from '../types';
import { authApi, ApiError } from '../services/api';

interface AuthContextType extends AuthState {
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string) => Promise<void>;
    loginWithGoogle: (idToken: string) => Promise<void>;
    logout: () => void;
    error: string | null;
    clearError: () => void;
    sessionToken: string | null;
    getSessionToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true,
    });
    const [error, setError] = useState<string | null>(null);
    const [sessionToken, setSessionToken] = useState<string | null>(null);

    // Check for existing session on mount
    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        const savedToken = localStorage.getItem('sessionToken');

        if (savedUser && savedToken) {
            try {
                const user = JSON.parse(savedUser);
                setAuthState({
                    user,
                    isAuthenticated: true,
                    isLoading: false,
                });
                setSessionToken(savedToken);
            } catch (err) {
                localStorage.removeItem('user');
                localStorage.removeItem('sessionToken');
                setAuthState(prev => ({ ...prev, isLoading: false }));
                setSessionToken(null);
            }
        } else {
            // Clear invalid sessions
            if (savedUser && !savedToken) {
                localStorage.removeItem('user');
            }
            if (savedToken && !savedUser) {
                localStorage.removeItem('sessionToken');
            }
            setAuthState(prev => ({ ...prev, isLoading: false }));
            setSessionToken(null);
        }
    }, []);

    const login = async (email: string, password: string) => {
        try {
            setError(null);
            setAuthState(prev => ({ ...prev, isLoading: true }));

            const response = await authApi.login({ email, password });

            setAuthState({
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
            });

            // Save to localStorage for persistence
            localStorage.setItem('user', JSON.stringify(response.user));
            localStorage.setItem('sessionToken', response.session.session_id);
            setSessionToken(response.session.session_id);
        } catch (err) {
            setAuthState(prev => ({ ...prev, isLoading: false }));
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Login failed. Please try again.');
            }
            throw err;
        }
    };

    const register = async (email: string, password: string) => {
        try {
            setError(null);
            setAuthState(prev => ({ ...prev, isLoading: true }));

            await authApi.register({ email, password });

            // After registration, log them in
            await login(email, password);
        } catch (err) {
            setAuthState(prev => ({ ...prev, isLoading: false }));
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Registration failed. Please try again.');
            }
            throw err;
        }
    };

    const loginWithGoogle = async (idToken: string) => {
        try {
            setError(null);
            setAuthState(prev => ({ ...prev, isLoading: true }));

            const response = await authApi.loginWithGoogle(idToken);

            setAuthState({
                user: response.user,
                isAuthenticated: true,
                isLoading: false,
            });

            // Save to localStorage for persistence
            localStorage.setItem('user', JSON.stringify(response.user));
            localStorage.setItem('sessionToken', response.session.session_id);
            setSessionToken(response.session.session_id);
        } catch (err) {
            setAuthState(prev => ({ ...prev, isLoading: false }));
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('Google sign-in failed. Please try again.');
            }
            throw err;
        }
    };

    const logout = async () => {
        try {
            if (sessionToken) {
                await authApi.logout();
            }
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            setAuthState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
            });
            localStorage.removeItem('user');
            localStorage.removeItem('sessionToken');
            setSessionToken(null);
            setError(null);
        }
    };

    const getSessionToken = () => {
        return sessionToken;
    };

    const clearError = () => {
        setError(null);
    };

    const value: AuthContextType = {
        ...authState,
        login,
        register,
        loginWithGoogle,
        logout,
        error,
        clearError,
        sessionToken,
        getSessionToken,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
